import React from 'react'
import { CBadge } from '@coreui/react-pro'

/** Labels match the Activate Games screen (GameActivations.js). */
export const GAME_TYPES = {
  1: 'Season Game',
  2: 'Tournament (Leagues)',
  3: 'Tournament (Indoor)',
  4: 'Match Contest',
}

export const gameTypeLabel = (type) => GAME_TYPES[type] || 'Other'

/**
 * Health is a STATE, so it wears the reserved status colours and always ships
 * with its label — never colour alone. Order is the order chips/legends show.
 */
export const HEALTH = [
  {
    key: 'growing',
    label: 'Growing',
    color: 'success',
    hint: 'Active users up 10% or more on the previous period',
  },
  { key: 'steady', label: 'Steady', color: 'info', hint: 'Within 10% of the previous period' },
  {
    key: 'declining',
    label: 'Declining',
    color: 'warning',
    hint: 'Active users down 10% or more on the previous period',
  },
  {
    key: 'dormant',
    label: 'Dormant',
    color: 'danger',
    hint: 'Season still running, but nobody opened the app for it this period',
  },
  { key: 'new', label: 'New', color: 'primary', hint: 'Game created inside this period' },
  {
    key: 'completed',
    label: 'Completed',
    color: 'secondary',
    hint: 'Season finished and quiet — expected',
  },
]

export const healthMeta = (key) => HEALTH.find((h) => h.key === key) || HEALTH[1]

export const HealthBadge = ({ health }) => {
  const meta = healthMeta(health)
  return (
    <CBadge color={meta.color} shape="rounded-pill" className="px-2 py-1" title={meta.hint}>
      {meta.label}
    </CBadge>
  )
}

/** Canvas cannot resolve CSS variables, so status colours are read at draw time. */
export const cssToken = (name, fallback) => {
  if (typeof document === 'undefined') return fallback
  const value = getComputedStyle(document.documentElement).getPropertyValue(`--cui-${name}`)
  return value && value.trim() ? value.trim() : fallback
}

export const formatMoney = (value) =>
  `$${(Number(value) || 0).toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })}`

/** "3 min ago" / "5 h ago" / "12 Oct". Input is a UTC "Y-m-d H:i:s" string. */
export const timeAgo = (utc) => {
  if (!utc) return null
  const ts = Date.parse(`${String(utc).replace(' ', 'T')}Z`)
  if (Number.isNaN(ts)) return null
  const mins = Math.round((Date.now() - ts) / 60000)
  if (mins < 1) return 'just now'
  if (mins < 60) return `${mins} min ago`
  const hours = Math.round(mins / 60)
  if (hours < 24) return `${hours} h ago`
  const days = Math.round(hours / 24)
  if (days < 14) return `${days} d ago`
  return new Date(ts).toLocaleDateString(undefined, { day: 'numeric', month: 'short' })
}

export const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']

export const hourLabel = (h) => {
  if (h === 0) return '12am'
  if (h === 12) return '12pm'
  return h < 12 ? `${h}am` : `${h - 12}pm`
}

/** Pull a human message out of an axios error or a { success:false } envelope. */
export const errorMessage = (err) =>
  err?.response?.data?.message || err?.message || 'Request failed.'

/** UTC "Y-m-d H:i:s" → "5 Nov, 6:42 pm" on the viewer's clock. */
export const formatDateTime = (utc) => {
  if (!utc) return '—'
  const ts = Date.parse(`${String(utc).replace(' ', 'T')}Z`)
  if (Number.isNaN(ts)) return '—'
  return new Date(ts).toLocaleString(undefined, {
    day: 'numeric',
    month: 'short',
    hour: 'numeric',
    minute: '2-digit',
  })
}

export const deviceLabel = (type) =>
  type === 'ios' ? 'iPhone' : type === 'android' ? 'Android' : 'Unknown device'

/** "iPhone · iPhone 15 · v2.4.1" — what the person is actually running. */
export const DeviceCell = ({ row }) => (
  <>
    <div className="fw-semibold">{deviceLabel(row.device_type)}</div>
    <div className="small text-medium-emphasis text-truncate pa-device-sub">
      {[
        row.device_model,
        row.os_version && `OS ${row.os_version}`,
        row.app_version && `v${row.app_version}`,
      ]
        .filter(Boolean)
        .join(' · ') || '—'}
    </div>
  </>
)

export const OnlineDot = ({ online }) =>
  online ? <span className="pa-online-dot" title="Online now" aria-label="Online now" /> : null
